import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Button, Divider, Form, Input, Space, Typography } from 'antd';
import { isAxiosError } from 'axios';
import { queryClient } from 'src/query-client';
import { apiClient } from '@api/client';
import CopyableInput from '@components/CopyableInput/CopyableInput';
import type { SendTestWebhookEventErrorDto } from '@api/Api';
import type { InstalledIntegrationDetailsProps } from '@pages/AdminSettings/components/IntegrationTab/constants';
import {
  ALL_EVENT_VALUES,
  EventTypesInput,
  HeadersInput,
  SignatureVerificationGuide,
  WebhookHeader,
  WebhookEventType,
  normalizeWebhookUrl,
  sanitizeEventTypes,
  validateWebhookHeaders,
  webhookHeadersToInput,
  webhookHeadersToPayload,
  validateWebhookUrl
} from '@pages/AdminSettings/components/IntegrationTab/webhook-shared';
import { nonNull } from '@helpers/non-null';

const { Title, Paragraph } = Typography;

interface UpdateWebhookForm {
  url: string;
  eventTypes: WebhookEventType[];
  headers: WebhookHeader[];
}

export default function WebhookInstalledDetails({
  integration,
  onClose
}: InstalledIntegrationDetailsProps) {
  const { data } = useQuery({
    suspense: true,
    queryKey: ['webhook', 'integration', integration.id],
    queryFn: async () => {
      const { data } = await apiClient.webhook.webhookControllerGetIntegration({
        integrationId: integration.id
      });
      return data;
    }
  });

  const [form] = Form.useForm<UpdateWebhookForm>();
  const [isTouched, setIsTouched] = useState(false);
  const [rotatedSecret, setRotatedSecret] = useState<string>('');

  const webhook = nonNull(data);
  const initialValues = useMemo(() => {
    const types =
      webhook.eventTypes === null ? ALL_EVENT_VALUES : webhook.eventTypes;
    return {
      url: webhook.url,
      eventTypes: sanitizeEventTypes(types),
      headers: webhookHeadersToInput(webhook.headers)
    };
  }, [webhook.eventTypes, webhook.headers, webhook.url]);

  const updateWebhook = useMutation({
    mutationFn: async (values: UpdateWebhookForm) => {
      await apiClient.webhook.webhookControllerUpdateIntegration({
        integrationId: integration.id,
        url: values.url,
        eventTypes: ALL_EVENT_VALUES.every((eventType) =>
          values.eventTypes.includes(eventType)
        )
          ? null
          : values.eventTypes,
        headers: webhookHeadersToPayload(values.headers)
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['webhook', 'integration', integration.id]
      });
      onClose();
    }
  });

  const rotateSecret = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.webhook.webhookControllerRotateSecret({
        integrationId: integration.id
      });
      return data;
    },
    onSuccess: (result) => {
      setRotatedSecret(result.secret);
    }
  });

  const sendTestEvent = useMutation({
    mutationFn: async () => {
      const values = await form.validateFields(['url', 'headers']);
      await apiClient.webhook.webhookControllerSendTestEvent({
        integrationId: integration.id,
        url: normalizeWebhookUrl(values.url),
        headers: webhookHeadersToPayload(values.headers)
      });
    }
  });

  const testEventError = useMemo(() => {
    const err = sendTestEvent.error;
    if (!err) return null;
    if (isAxiosError(err)) {
      const payload = err.response?.data;
      if (payload?.errorCode === 'send_test_event_error') {
        const data = payload as SendTestWebhookEventErrorDto;
        return `Webhook endpoint responded with status ${data.statusCode}${
          data.statusText ? `(${data.statusText})` : ''
        }`;
      }
      return `Unknown error occurred`;
    }
  }, [sendTestEvent.error]);

  const hasNoEvents = initialValues.eventTypes.length === 0;

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0
      }}
    >
      <div style={{ paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
        <Title level={3} style={{ marginBottom: 0 }}>
          Webhooks
        </Title>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }}>
        <Paragraph style={{ maxWidth: 600, marginTop: 12 }}>
          Update where StreamWork sends webhook events and choose which event
          types are delivered.
        </Paragraph>
        {updateWebhook.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message="Failed to update webhook settings"
            description="Please review your values and try again."
          />
        ) : null}
        {rotateSecret.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message="Failed to rotate signing secret"
            description="Please try again."
          />
        ) : null}

        <Form<UpdateWebhookForm>
          form={form}
          layout="vertical"
          initialValues={initialValues}
          onFinish={(values) => updateWebhook.mutate(values)}
          onFieldsChange={() => setIsTouched(form.isFieldsTouched())}
          onValuesChange={() => sendTestEvent.reset()}
        >
          <Form.Item
            label="Webhooks URL"
            extra="Use a public HTTPS endpoint from your backend, Zapier, Make.com, or n8n."
          >
            <Space.Compact style={{ width: '100%' }} block>
              <Form.Item
                noStyle
                name="url"
                rules={[
                  { required: true, message: 'Webhooks URL is required' },
                  {
                    validator: (_, value: string | undefined) => {
                      return validateWebhookUrl(value);
                    }
                  }
                ]}
              >
                <Input
                  placeholder="https://example.com/webhooks/streamwork"
                  onPaste={(event) => {
                    const pastedText = event.clipboardData.getData('text');
                    const normalized = normalizeWebhookUrl(pastedText);
                    if (normalized !== pastedText) {
                      event.preventDefault();
                      form.setFieldValue('url', normalized);
                    }
                  }}
                  onBlur={() => {
                    const currentValue = form.getFieldValue('url') as
                      | string
                      | undefined;
                    if (!currentValue) {
                      return;
                    }
                    form.setFieldValue(
                      'url',
                      normalizeWebhookUrl(currentValue)
                    );
                  }}
                />
              </Form.Item>

              <Button
                type="dashed"
                onClick={() => sendTestEvent.mutate()}
                loading={sendTestEvent.isPending}
                size="small"
              >
                Test
              </Button>
            </Space.Compact>
          </Form.Item>

          {sendTestEvent.isSuccess ? (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
              message="Test webhook event sent successfully."
            />
          ) : null}

          {testEventError ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="Failed to send test webhook event"
              description={testEventError}
            />
          ) : null}

          <Form.Item
            name="headers"
            label="Custom headers"
            validateTrigger={['onBlur', 'onSubmit']}
            rules={[
              {
                validator: (_, value: WebhookHeader[] | undefined) =>
                  validateWebhookHeaders(value)
              }
            ]}
            extra="Optional headers added to each webhook request."
          >
            <HeadersInput />
          </Form.Item>

          <Form.Item
            name="eventTypes"
            rules={[
              {
                required: true,
                type: 'array',
                min: 1,
                message: 'Select at least one event'
              }
            ]}
            extra={
              hasNoEvents
                ? 'No recognized event types are currently configured. Choose at least one event before saving.'
                : undefined
            }
          >
            <EventTypesInput />
          </Form.Item>
        </Form>

        <Divider />

        <Title level={4}>Rotate signing secret</Title>
        <Paragraph style={{ maxWidth: 600 }}>
          Rotating the signing secret immediately invalidates the old secret.
          Update your webhooks consumer before processing more events. If you
          need a safer transition, create another webhook integration with the
          same URL so you can roll out the new secret while the existing secret
          remains active.
        </Paragraph>
        <Button
          danger
          type="default"
          loading={rotateSecret.isPending}
          onClick={() => rotateSecret.mutate()}
          style={{ width: 'fit-content', marginBottom: rotatedSecret ? 12 : 0 }}
        >
          Rotate signing secret
        </Button>
        {rotatedSecret ? (
          <>
            <Paragraph style={{ marginBottom: 8, marginTop: 12 }}>
              New signing secret
            </Paragraph>
            <CopyableInput
              readOnly
              size="small"
              value={rotatedSecret}
              style={{ marginBottom: 12 }}
            />
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Save this new secret now"
              description="For security reasons this new signing secret will not be shown again. Store it in your password manager or secrets vault."
            />
          </>
        ) : null}

        <Divider />

        <Title level={4}>Strongly recommended: validate event signatures</Title>
        <Paragraph style={{ marginBottom: 12 }}>
          Signature validation lets your endpoint verify that each webhook
          request was sent by StreamWork and not forged by a third party.
        </Paragraph>
        <SignatureVerificationGuide />
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          paddingTop: 12,
          borderTop: '1px solid #f0f0f0'
        }}
      >
        <Button onClick={onClose}>Close</Button>
        <Button
          type="primary"
          loading={updateWebhook.isPending}
          onClick={() => form.submit()}
          disabled={!isTouched && !hasNoEvents}
        >
          Update Settings
        </Button>
      </div>
    </div>
  );
}
